import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, LogOut, Plus, Save, Trash2, Truck, Package, Mail, Users, ShoppingBag, Upload, X, GripVertical, CreditCard, CopyIcon, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${(+n || 0).toLocaleString("en-IN")}`;
const ORDER_STATUSES = ["pending_payment","paid","packed","shipped","delivered","cancelled"];
const MAX_IMAGES = 8;

// ---------- Admin shell ----------
function AdminShell({ children, onSignOut }) {
  return <>
    <header className="admin-header">
      <Link to="/admin" className="logo">ReFleX<span>&</span>Co<small>ADMIN</small></Link>
      <nav>
        <Link to="/admin/orders" data-testid="admin-nav-orders"><Package size={14}/> Orders</Link>
        <Link to="/admin/products" data-testid="admin-nav-products"><ShoppingBag size={14}/> Products</Link>
        <Link to="/admin/contact" data-testid="admin-nav-contact"><Mail size={14}/> Messages</Link>
        <Link to="/admin/subscribers" data-testid="admin-nav-subs"><Users size={14}/> Subscribers</Link>
        <Link to="/admin/payment" data-testid="admin-nav-payment"><CreditCard size={14}/> Payment</Link>
      </nav>
      <button onClick={onSignOut} className="icon-button" data-testid="admin-sign-out"><LogOut size={16}/> Sign out</button>
    </header>
    <main className="admin-main">{children}</main>
  </>;
}

// ---------- Orders ----------
function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ status: "new", tracking_number: "", tracking_url: "" });
  const [msg, setMsg] = useState("");
  const load = () => axios.get(`${API}/admin/orders`).then(r=>setOrders(r.data));
  useEffect(() => { load(); }, []);
  const open = (o) => { setEditing(o); setDraft({ status: o.status||"pending_payment", tracking_number: o.tracking_number||"", tracking_url: o.tracking_url||"" }); setMsg(""); };
  const save = async () => {
    setMsg("");
    try {
      await axios.patch(`${API}/admin/orders/${editing.id}`, draft);
      let note = "Saved.";
      if (draft.status === "paid" && editing.status !== "paid") note = "Saved — payment confirmed, receipt and alert queued.";
      else if (draft.status === "shipped" && editing.status !== "shipped") note = "Saved — shipping email queued.";
      setMsg(note);
      await load();
    } catch { setMsg("Could not save — check the tracking URL (https only)."); }
  };
  return <div className="admin-section">
    <h2>Orders <small>{orders.length}</small></h2>
    {!orders.length && <p className="muted">No orders yet.</p>}
    <div className="admin-table" data-testid="admin-orders-table">
      {orders.map(o => <button className={`admin-row ${editing?.id===o.id?'active':''}`} key={o.id} onClick={()=>open(o)} data-testid={`admin-order-${o.order_number}`}>
        <div><strong>{o.order_number}</strong><span className={`chip chip-${o.status}`}>{o.status}</span></div>
        <div>{o.name} · {o.city}</div>
        <div><em>{money(o.total)}</em></div>
      </button>)}
    </div>
    {editing && <aside className="admin-drawer" data-testid="admin-order-drawer">
      <p className="eyebrow">ORDER {editing.order_number}</p>
      <h3>{editing.name}</h3>
      <p className="muted">{editing.email} · {editing.phone}</p>
      <p className="muted">{editing.address}, {editing.city}, {editing.state} {editing.pincode}</p>
      <div className="rule"/>
      <h4>Items</h4>
      {editing.items?.map((i,idx)=><p key={idx} className="muted">{i.product_name || i.product_id} × {i.quantity}{i.size?` · size ${i.size}`:""} — {money((i.unit_price||0)*i.quantity)}</p>)}
      <p><strong>Total · {money(editing.total)}</strong></p>
      <div className="rule"/>
      <label>Status
        <select value={draft.status} onChange={e=>setDraft({...draft,status:e.target.value})} data-testid="admin-order-status">
          {ORDER_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label>Tracking number<input value={draft.tracking_number} onChange={e=>setDraft({...draft,tracking_number:e.target.value})} data-testid="admin-order-tracking-number"/></label>
      <label>Tracking URL (https only)<input value={draft.tracking_url} onChange={e=>setDraft({...draft,tracking_url:e.target.value})} placeholder="https://…" data-testid="admin-order-tracking-url"/></label>
      <button className="button button-light" onClick={save} data-testid="admin-order-save"><Truck size={14}/> Save & notify</button>
      {msg && <p className="hint" data-testid="admin-order-msg">{msg}</p>}
    </aside>}
  </div>;
}

// ---------- Products ----------
const EMPTY_PRODUCT = { name:"", category:"T-Shirts", collection:"New Arrivals", price:1990, color:"", sizes:["S","M","L"], stock:10, description:"", images:[], badge:"" };

function ImageGallery({ images, productId, onChange }) {
  const inputRef = useRef(null);
  const [uploads, setUploads] = useState([]);  // in-flight: [{tmpId, progress, error}]
  const [dragOver, setDragOver] = useState(false);
  const [removed, setRemoved] = useState(null);  // {url, idx, timer}
  const uid = productId || "new";

  const upload = async (file) => {
    const tmpId = Math.random().toString(36).slice(2);
    setUploads(u => [...u, { tmpId, progress: 0 }]);
    const form = new FormData(); form.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/images/upload?product_id=${encodeURIComponent(uid)}`, form, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 50;
          setUploads(u => u.map(x => x.tmpId===tmpId ? {...x, progress: pct} : x));
        }
      });
      setUploads(u => u.filter(x => x.tmpId !== tmpId));
      onChange([...images, r.data.url]);
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      setUploads(u => u.map(x => x.tmpId===tmpId ? {...x, error: msg} : x));
      setTimeout(() => setUploads(u => u.filter(x => x.tmpId !== tmpId)), 3500);
    }
  };

  const handleFiles = (files) => {
    const list = Array.from(files || []);
    const roomLeft = MAX_IMAGES - images.length - uploads.length;
    list.slice(0, Math.max(0, roomLeft)).forEach(upload);
  };

  const removeAt = (idx) => {
    const url = images[idx];
    const next = images.filter((_, i) => i !== idx);
    if (removed?.timer) clearTimeout(removed.timer);
    const timer = setTimeout(() => setRemoved(null), 5000);
    setRemoved({ url, idx, timer });
    onChange(next);
  };

  const undo = () => {
    if (!removed) return;
    const next = images.slice();
    next.splice(removed.idx, 0, removed.url);
    onChange(next);
    if (removed.timer) clearTimeout(removed.timer);
    setRemoved(null);
  };

  // simple drag-reorder
  const dragFrom = useRef(null);
  const onDragStart = (i) => () => { dragFrom.current = i; };
  const onDragOverIdx = (i) => (e) => { e.preventDefault(); };
  const onDropIdx = (i) => (e) => {
    e.preventDefault();
    const from = dragFrom.current;
    if (from == null || from === i) return;
    const next = images.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onChange(next);
    dragFrom.current = null;
  };

  const onZoneDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  return <div className="image-gallery" data-testid="admin-image-gallery">
    <div
      className={`image-dropzone ${dragOver?"active":""}`}
      data-testid="admin-image-dropzone"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e)=>{e.preventDefault();setDragOver(true);}}
      onDragLeave={()=>setDragOver(false)}
      onDrop={onZoneDrop}
    >
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e)=>handleFiles(e.target.files)} data-testid="admin-image-input"/>
      <Upload size={22}/>
      <p className="eyebrow">Drop photos or click to browse</p>
      <small className="muted">JPG, PNG, or WEBP · max 5 MB · up to {MAX_IMAGES} per product</small>
      <small className="muted">Soft delete — files remain stored and can be restored by the team</small>
    </div>
    <div className="image-thumbs">
      {images.map((url, i) => (
        <div key={url+i} className={`image-thumb ${i===0?"primary":""}`} draggable onDragStart={onDragStart(i)} onDragOver={onDragOverIdx(i)} onDrop={onDropIdx(i)} data-testid={`admin-image-thumb-${i}`}>
          <img src={url} alt=""/>
          {i===0 && <span className="chip chip-primary">PRIMARY</span>}
          <button type="button" className="handle" aria-label="Drag to reorder"><GripVertical size={14}/></button>
          <button type="button" className="remove" onClick={()=>removeAt(i)} data-testid={`admin-image-remove-${i}`}><X size={14}/></button>
        </div>
      ))}
      {uploads.map(u => (
        <div key={u.tmpId} className={`image-thumb uploading ${u.error?"error":""}`} data-testid="admin-image-uploading">
          <div className="progress"><div style={{width:`${u.progress}%`}}/></div>
          {u.error ? <small className="error-strip">{u.error}</small> : <small className="muted">{u.progress}%</small>}
        </div>
      ))}
    </div>
    {removed && <div className="undo-toast" data-testid="admin-image-undo"><span>Image removed.</span><button type="button" onClick={undo} data-testid="admin-image-undo-button">Undo</button></div>}
  </div>;
}

function AdminProducts() {
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState(EMPTY_PRODUCT);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => axios.get(`${API}/admin/products`).then(r=>setList(r.data));
  useEffect(() => { load(); }, []);
  const change = (k, v) => setDraft(d => ({...d, [k]: v}));
  const setSizes = (s) => change("sizes", s.split(",").map(x=>x.trim()).filter(Boolean));
  const save = async (e) => {
    e.preventDefault(); setMsg("");
    if (!draft.images || draft.images.length === 0) { setMsg("Add at least one product image."); return; }
    try {
      if (editingId) await axios.patch(`${API}/admin/products/${editingId}`, draft);
      else await axios.post(`${API}/admin/products`, draft);
      setDraft(EMPTY_PRODUCT); setEditingId(null); setMsg("Saved.");
      await load();
    } catch (e) { setMsg(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save."); }
  };
  const edit = (p) => { setEditingId(p.id); setDraft({...EMPTY_PRODUCT, ...p, images: p.images && p.images.length ? p.images : (p.image?[p.image]:[])}); setMsg(""); };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`);
    if (editingId===p.id) { setEditingId(null); setDraft(EMPTY_PRODUCT); }
    await load();
  };
  return <div className="admin-section">
    <div className="admin-section-head"><h2>Products <small>{list.length}</small></h2>
      {editingId && <button className="text-link" onClick={()=>{setEditingId(null);setDraft(EMPTY_PRODUCT);}} data-testid="admin-product-cancel">Cancel edit</button>}
    </div>
    <form onSubmit={save} className="admin-form" data-testid="admin-product-form">
      <label>Name<input value={draft.name} onChange={e=>change("name",e.target.value)} required data-testid="admin-product-name"/></label>
      <label>Category<input value={draft.category} onChange={e=>change("category",e.target.value)} required data-testid="admin-product-category"/></label>
      <label>Collection<input value={draft.collection} onChange={e=>change("collection",e.target.value)} required data-testid="admin-product-collection"/></label>
      <label>Price (INR)<input type="number" value={draft.price} onChange={e=>change("price",+e.target.value)} required data-testid="admin-product-price"/></label>
      <label>Colour<input value={draft.color} onChange={e=>change("color",e.target.value)} required data-testid="admin-product-color"/></label>
      <label>Sizes (comma)<input value={draft.sizes.join(",")} onChange={e=>setSizes(e.target.value)} required data-testid="admin-product-sizes"/></label>
      <label>Stock<input type="number" value={draft.stock} onChange={e=>change("stock",+e.target.value)} required data-testid="admin-product-stock"/></label>
      <label>Badge<input value={draft.badge} onChange={e=>change("badge",e.target.value)} placeholder="NEW · SIGNATURE · empty" data-testid="admin-product-badge"/></label>
      <label className="wide">Product images<ImageGallery images={draft.images || []} productId={editingId} onChange={(images)=>change("images", images)}/></label>
      <label className="wide">Description<textarea value={draft.description} onChange={e=>change("description",e.target.value)} required data-testid="admin-product-description"/></label>
      <button className="button button-light" data-testid="admin-product-save">{editingId?<><Save size={14}/> Update</>:<><Plus size={14}/> Add product</>}</button>
      {msg && <p className="hint" data-testid="admin-product-msg">{msg}</p>}
    </form>
    <div className="admin-grid" data-testid="admin-products-grid">{list.map(p=>
      <article key={p.id} className="admin-card">
        <img src={(p.images && p.images[0]) || p.image} alt=""/>
        <div><strong>{p.name}</strong><span>{p.category} · {money(p.price)} · stock {p.stock}</span></div>
        <div className="admin-card-actions">
          <button onClick={()=>edit(p)} data-testid={`admin-edit-${p.id}`}>Edit</button>
          <button onClick={()=>remove(p)} className="danger" data-testid={`admin-delete-${p.id}`}><Trash2 size={13}/></button>
        </div>
      </article>)}
    </div>
  </div>;
}

// ---------- Payment settings ----------
function AdminPayment() {
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState("");
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const load = () => axios.get(`${API}/admin/settings/payment`).then(r=>{ setCfg(r.data); setEditing(!r.data.has_webhook_secret); });
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await axios.put(`${API}/admin/settings/payment/webhook-secret`, { secret });
      setSecret(""); setEditing(false); setMsg("Webhook secret saved.");
      await load();
    } catch (e) { setMsg(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save."); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the webhook secret? Verification will pause until a new one is saved.")) return;
    await axios.delete(`${API}/admin/settings/payment/webhook-secret`);
    setMsg("Webhook secret removed.");
    await load();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(cfg.webhook_url); setCopied(true); setTimeout(()=>setCopied(false), 2000); } catch {}
  };
  if (!cfg) return <div className="admin-section"><h2>Payment</h2><p className="muted">Loading…</p></div>;
  const since = cfg.webhook_secret_updated_at ? new Date(cfg.webhook_secret_updated_at).toLocaleString() : null;
  return <div className="admin-section payment-section">
    <h2>Payment settings <small>Razorpay</small></h2>
    <div className="payment-card" data-testid="admin-payment-card">
      <div className="payment-row">
        <strong>Public key</strong>
        <code data-testid="admin-payment-key-id">{cfg.key_id || "—"}</code>
      </div>
      <div className="payment-row">
        <strong>Secret key</strong>
        <span className="muted" data-testid="admin-payment-secret-state">{cfg.secret_key_configured ? "Configured" : "Not set"}</span>
      </div>
      <div className="payment-row">
        <strong>Webhook URL</strong>
        <div className="webhook-url">
          <code data-testid="admin-webhook-url">{cfg.webhook_url}</code>
          <button type="button" className="copy-btn" onClick={copy} data-testid="admin-webhook-copy">{copied ? <><Check size={13}/> Copied</> : <><CopyIcon size={13}/> Copy</>}</button>
        </div>
      </div>
      <div className="payment-row">
        <strong>Webhook secret</strong>
        {editing ? (
          <form onSubmit={save} className="webhook-secret-form">
            <input type="password" value={secret} onChange={e=>setSecret(e.target.value)} placeholder="Paste the secret from Razorpay" minLength={8} required data-testid="admin-webhook-secret-input"/>
            <button type="submit" className="button button-light" data-testid="admin-webhook-secret-save">Save <Save size={14}/></button>
            {cfg.has_webhook_secret && <button type="button" onClick={()=>{setEditing(false);setSecret("");}} className="text-link">Cancel</button>}
          </form>
        ) : (
          <div className="webhook-secret-status">
            <span data-testid="admin-webhook-secret-status">Saved {since && <>· {since}</>} {cfg.webhook_secret_source && <small className="muted">({cfg.webhook_secret_source})</small>}</span>
            <button type="button" className="link-btn" onClick={()=>setEditing(true)} data-testid="admin-webhook-secret-replace">Replace</button>
            <button type="button" className="link-btn danger" onClick={remove} data-testid="admin-webhook-secret-remove">Remove</button>
          </div>
        )}
      </div>
      <div className="payment-row">
        <strong>Last captured payment</strong>
        <span data-testid="admin-payment-last" className="muted">{cfg.last_captured_at ? new Date(cfg.last_captured_at).toLocaleString() : "—"}</span>
      </div>
      {msg && <p className="hint" data-testid="admin-payment-msg">{msg}</p>}
    </div>
    <p className="muted small">Public and Secret key live in the server environment (`.env`). To rotate them, edit `backend/.env` and restart the backend — intentionally kept outside the console for payments-grade security.</p>
  </div>;
}

// ---------- Submissions / subscribers ----------
function AdminList({ endpoint, title, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/${endpoint}`).then(r=>setRows(r.data)).catch(()=>{}); }, [endpoint]);
  return <div className="admin-section">
    <h2>{title} <small>{rows.length}</small></h2>
    {!rows.length && <p className="muted">Nothing here yet.</p>}
    <div className="admin-table" data-testid={testid}>
      {rows.map((r,i)=><div className="admin-row static" key={r.id || i}>
        {columns.map(c => <div key={c.key}><strong>{c.label}</strong><span>{r[c.key] || "—"}</span></div>)}
      </div>)}
    </div>
  </div>;
}

// ---------- Top-level (RequireAdmin in App.js already gates this) ----------
export default function Admin() {
  const nav = useNavigate();
  const signOut = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    nav("/sign-in", { replace: true });
    // Hard reload clears AuthContext so admin link disappears
    setTimeout(() => window.location.reload(), 100);
  };
  return <AdminShell onSignOut={signOut}>
    <Routes>
      <Route index element={<Navigate to="orders" replace/>}/>
      <Route path="orders" element={<AdminOrders/>}/>
      <Route path="products" element={<AdminProducts/>}/>
      <Route path="contact" element={<AdminList endpoint="admin/contact-submissions" title="Support messages" testid="admin-contact-list" columns={[{key:"created_at",label:"When"},{key:"name",label:"Name"},{key:"email",label:"Email"},{key:"subject",label:"Subject"},{key:"message",label:"Message"}]}/>}/>
      <Route path="subscribers" element={<AdminList endpoint="admin/newsletter-subscribers" title="Newsletter subscribers" testid="admin-subs-list" columns={[{key:"created_at",label:"When"},{key:"email",label:"Email"}]}/>}/>
      <Route path="payment" element={<AdminPayment/>}/>
    </Routes>
  </AdminShell>;
}
